// src/contexts/AuthContext.jsx - Admin authentication context
import React, { createContext, useContext, useState, useEffect } from 'react';
import { ADMIN_UNAUTHORIZED_EVENT, adminAuthAPI } from '../api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const logout = () => {
    // Clear admin session data
    localStorage.removeItem('spacejet-admin-token');
    localStorage.removeItem('spacejet-admin-user');
    sessionStorage.removeItem('spacejet-linktrust-import-credentials');

    setAdminUser(null);
    setIsAuthenticated(false);
  };

  useEffect(() => {
    let isMounted = true;

    const verifyExistingSession = async () => {
      const adminToken = localStorage.getItem('spacejet-admin-token');

      if (!adminToken) {
        if (isMounted) setIsLoading(false);
        return;
      }

      try {
        const response = await adminAuthAPI.verify(adminToken);
        const verifiedAdmin = response?.data?.admin;

        if (!verifiedAdmin || response?.data?.type !== 'admin') {
          throw new Error('Invalid admin session');
        }

        localStorage.setItem('spacejet-admin-user', JSON.stringify(verifiedAdmin));

        if (isMounted) {
          setAdminUser(verifiedAdmin);
          setIsAuthenticated(true);
        }
      } catch (error) {
        localStorage.removeItem('spacejet-admin-token');
        localStorage.removeItem('spacejet-admin-user');

        if (isMounted) {
          setAdminUser(null);
          setIsAuthenticated(false);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    verifyExistingSession();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      logout();
    };

    window.addEventListener(ADMIN_UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => {
      window.removeEventListener(ADMIN_UNAUTHORIZED_EVENT, handleUnauthorized);
    };
  }, []);

  const login = async (email, password) => {
    try {
      // Make real API call to backend admin login endpoint
      const response = await adminAuthAPI.login({ email, password });
      const authData = response?.data;

      if (!authData?.token || !authData?.admin) {
        throw new Error('Invalid admin login response');
      }
      
      // Store admin session data (separate from user sessions)
      localStorage.setItem('spacejet-admin-token', authData.token);
      localStorage.setItem('spacejet-admin-user', JSON.stringify(authData.admin));
      
      setAdminUser(authData.admin);
      setIsAuthenticated(true);
      
      return { success: true };
    } catch (error) {
      const wrappedError = new Error(error.message || 'Invalid admin credentials');
      wrappedError.code = error.code;
      throw wrappedError;
    }
  };

  const value = {
    isAuthenticated,
    adminUser,
    isLoading,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
